import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Globe, Search } from 'lucide-react';

/* ---- Languages (label = native name, english = English name, flag = emoji) ---- */
const LANGS = [
  { code: 'en', flag: '🇬🇧', label: 'English', english: 'English' },
  { code: 'de', flag: '🇩🇪', label: 'Deutsch', english: 'German' },
  { code: 'fr', flag: '🇫🇷', label: 'Français', english: 'French' },
  { code: 'it', flag: '🇮🇹', label: 'Italiano', english: 'Italian' },
  { code: 'es', flag: '🇪🇸', label: 'Español', english: 'Spanish' },
  { code: 'nl', flag: '🇳🇱', label: 'Nederlands', english: 'Dutch' },
  { code: 'pl', flag: '🇵🇱', label: 'Polski', english: 'Polish' },
  { code: 'pt', flag: '🇵🇹', label: 'Português', english: 'Portuguese' },
  { code: 'sv', flag: '🇸🇪', label: 'Svenska', english: 'Swedish' },
  { code: 'no', flag: '🇳🇴', label: 'Norsk', english: 'Norwegian' },
  { code: 'da', flag: '🇩🇰', label: 'Dansk', english: 'Danish' },
  { code: 'fi', flag: '🇫🇮', label: 'Suomi', english: 'Finnish' },
  { code: 'cs', flag: '🇨🇿', label: 'Čeština', english: 'Czech' },
  { code: 'ro', flag: '🇷🇴', label: 'Română', english: 'Romanian' },
  { code: 'hu', flag: '🇭🇺', label: 'Magyar', english: 'Hungarian' },
  { code: 'el', flag: '🇬🇷', label: 'Ελληνικά', english: 'Greek' },
  { code: 'ru', flag: '🇷🇺', label: 'Русский', english: 'Russian' },
  { code: 'ja', flag: '🇯🇵', label: '日本語', english: 'Japanese' },
  { code: 'zh-CN', flag: '🇨🇳', label: '中文', english: 'Chinese' },
  { code: 'hi', flag: '🇮🇳', label: 'हिन्दी', english: 'Hindi' },
  { code: 'ar', flag: '🇸🇦', label: 'العربية', english: 'Arabic' },
];

const setCookie = (value, expired = false) => {
  // Set on apex + on current path (covers subdomains and the current host).
  const expires = expired
    ? '; expires=Thu, 01 Jan 1970 00:00:00 UTC'
    : '';
  document.cookie = `googtrans=${value}${expires}; path=/; domain=.myshaktiverse.com`;
  document.cookie = `googtrans=${value}${expires}; path=/`;
};

const applyLanguage = (code) => {
  if (code === 'en') {
    setCookie('', true);
    try { localStorage.removeItem('selectedLanguage'); } catch (_) {}
  } else {
    setCookie(`/en/${code}`);
    try { localStorage.setItem('selectedLanguage', code); } catch (_) {}
  }
  window.location.reload();
};

const TranslateBar = () => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeCode, setActiveCode] = useState('en');
  const wrapperRef = useRef(null);

  // On mount: read saved language to render the current state in the trigger.
  useEffect(() => {
    try {
      const saved = localStorage.getItem('selectedLanguage');
      if (saved && LANGS.some((l) => l.code === saved)) setActiveCode(saved);
    } catch (_) { /* ignore */ }
  }, []);

  // Close on outside click.
  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return LANGS;
    return LANGS.filter(
      (l) =>
        l.label.toLowerCase().includes(q) ||
        l.english.toLowerCase().includes(q) ||
        l.code.toLowerCase().includes(q)
    );
  }, [query]);

  const active = LANGS.find((l) => l.code === activeCode) || LANGS[0];
  const showActiveInTrigger = activeCode && activeCode !== 'en';

  return (
    <div
      className="fixed left-0 right-0 z-[49] bg-background/80 backdrop-blur-md border-b border-primary/20 notranslate"
      style={{ top: 113, height: 36 }}
      data-testid="translate-bar"
    >
      <div className="h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-end">
        <div className="relative" ref={wrapperRef}>
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="inline-flex items-center gap-1.5 border border-primary/30 text-primary/85 text-xs rounded-full px-3 py-1 hover:bg-primary/10 transition-colors"
            data-testid="translate-trigger"
            aria-haspopup="listbox"
            aria-expanded={open}
          >
            {showActiveInTrigger ? (
              <>
                <span aria-hidden="true">{active.flag}</span>
                <span>{active.label}</span>
              </>
            ) : (
              <>
                <Globe size={14} />
                <span>Translate</span>
              </>
            )}
          </button>

          {open && (
            <div
              className="absolute right-0 mt-2 bg-card border border-primary/20 rounded-lg shadow-lg overflow-hidden"
              style={{
                width: 'min(260px, calc(100vw - 16px))',
              }}
              data-testid="translate-dropdown"
            >
              <div className="p-2 border-b border-primary/15 flex items-center gap-2">
                <Search size={13} className="text-primary/50 shrink-0" />
                <input
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search language..."
                  className="w-full bg-transparent text-primary text-xs placeholder-primary/40 outline-none"
                  autoFocus
                  data-testid="translate-search"
                />
              </div>
              <ul
                className="overflow-y-auto py-1"
                style={{ maxHeight: 280 }}
                role="listbox"
              >
                {filtered.length === 0 ? (
                  <li className="px-3 py-2 text-xs text-primary/50 italic">
                    No matches.
                  </li>
                ) : (
                  filtered.map((l) => {
                    const isActive = l.code === activeCode;
                    return (
                      <li
                        key={l.code}
                        role="option"
                        aria-selected={isActive}
                        onClick={() => applyLanguage(l.code)}
                        className={`flex items-baseline gap-2 px-3 py-2 cursor-pointer hover:bg-primary/10 ${
                          isActive ? 'text-primary' : 'text-primary/85'
                        }`}
                        data-testid={`translate-lang-${l.code}`}
                      >
                        <span className="text-base shrink-0" aria-hidden="true">{l.flag}</span>
                        <div className="flex flex-col min-w-0">
                          <span className="text-sm leading-tight">{l.label}</span>
                          <span className="text-[10px] uppercase tracking-wider text-primary/50 leading-tight">
                            {l.english}
                          </span>
                        </div>
                      </li>
                    );
                  })
                )}
              </ul>
            </div>
          )}
        </div>
      </div>

      {/* Hidden host element for Google's translate widget. We control language
          via the googtrans cookie; this <div> is required so element.js can
          attach without erroring. Visually hidden but kept in DOM. */}
      <div
        id="google_translate_element"
        style={{
          position: 'absolute',
          width: 1,
          height: 1,
          padding: 0,
          margin: -1,
          overflow: 'hidden',
          clip: 'rect(0,0,0,0)',
          whiteSpace: 'nowrap',
          border: 0,
        }}
        aria-hidden="true"
      />
    </div>
  );
};

export default TranslateBar;
